import { useEffect, useState } from "react";
import { api, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Upload, Download, Lock, Users as UsersIcon, Shield } from "lucide-react";

const VIS_META = { ALL_MEMBERS: { icon: UsersIcon, label: "All Members" }, PRIVATE_MEMBER: { icon: Lock, label: "Private" }, ADMIN_ONLY: { icon: Shield, label: "Admin Only" } };

export default function Documents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ title: "", category: "General", visibility: "ALL_MEMBERS", member_id: "" });

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); if (isAdmin) api.get("/members").then((r) => setMembers(r.data)).catch(() => {}); }, [isAdmin]);

  const upload = async () => {
    if (!file || !f.title) return toast.error("Title and file required");
    const fd = new FormData();
    fd.append("file", file); fd.append("title", f.title); fd.append("category", f.category); fd.append("visibility", f.visibility);
    if (f.visibility === "PRIVATE_MEMBER" && f.member_id) fd.append("member_id", f.member_id);
    try { await api.post("/documents", fd); toast.success("Document uploaded"); setOpen(false); setFile(null); setF({ title: "", category: "General", visibility: "ALL_MEMBERS", member_id: "" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const download = async (d) => {
    if (d.placeholder) return toast.info("Sample document — upload a file to replace.");
    try { const r = await api.get(`/files/${d.id}/download`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Unable to open"); }
  };
  const inputCls = "w-full panel rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#C5A059]";

  return (
    <div>
      <PageHeader eyebrow="Secure Area" title="Documents">
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="upload-doc-btn" className="btn-brass flex items-center gap-1.5 px-4 py-2.5 rounded-sm text-sm"><Upload size={15} /> Upload</button></DialogTrigger>
            <DialogContent className="bg-[#131316] border-[#28282D] text-[#F5F3EF]">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Upload Document</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><label className="eyebrow block mb-1.5">Title</label><input data-testid="doc-title" value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} className={inputCls} /></div>
                <div><label className="eyebrow block mb-1.5">Category</label><input value={f.category} onChange={(e) => setF((p) => ({ ...p, category: e.target.value }))} className={inputCls} /></div>
                <div><label className="eyebrow block mb-1.5">Visibility</label>
                  <select value={f.visibility} onChange={(e) => setF((p) => ({ ...p, visibility: e.target.value }))} className={inputCls} data-testid="doc-visibility">
                    <option value="ALL_MEMBERS">All Members</option><option value="PRIVATE_MEMBER">Private to Member</option><option value="ADMIN_ONLY">Admin Only</option>
                  </select>
                </div>
                {f.visibility === "PRIVATE_MEMBER" && (
                  <div><label className="eyebrow block mb-1.5">Member</label>
                    <select value={f.member_id} onChange={(e) => setF((p) => ({ ...p, member_id: e.target.value }))} className={inputCls}>
                      <option value="">Select…</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                )}
                <div><label className="eyebrow block mb-1.5">File</label><input type="file" onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file" className={inputCls} /></div>
                <button onClick={upload} data-testid="submit-doc" className="btn-brass w-full py-2.5 rounded-sm text-sm">Upload Document</button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {docs.map((d) => {
          const V = VIS_META[d.visibility] || VIS_META.ALL_MEMBERS;
          return (
            <button key={d.id} onClick={() => download(d)} data-testid={`doc-${d.id}`}
              className="panel rounded-sm p-5 text-left hover:border-[#C5A059] transition-colors flex items-start gap-4">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: "#1A1A1E" }}><FileText size={18} className="brass-text" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#E2DFD7] leading-snug">{d.title}</div>
                <div className="text-[0.65rem] text-[#8C867A] mt-1">{d.category} · {fmtDate(d.created_at)}</div>
                <div className="flex items-center gap-1 mt-2 text-[0.6rem] uppercase tracking-wider text-[#8C867A]"><V.icon size={11} /> {V.label}</div>
              </div>
              <Download size={14} className="text-[#5c5850] shrink-0" />
            </button>
          );
        })}
        {docs.length === 0 && <div className="text-sm text-[#8C867A] col-span-full py-10 text-center">No documents available.</div>}
      </div>
    </div>
  );
}
